import Joi from 'joi';
import { ExternalSourceType, ExternalSourceTypes } from 'src/actions/integrations/commonTypes';

/**
 * Validation schema building blocks. We extend and compose the blocks to build full validation schema.
 */

const optionalFlag = Joi.boolean().optional();
const requiredFlag = Joi.boolean().required();
const optionalString = Joi.string().optional();
const requiredString = Joi.string().trim().required();

const baseSchema = Joi.object({
  id: Joi.any().required(),
  enabled: requiredFlag,
  createdAt: optionalString,
  updatedAt: optionalString,
});

const appBaseSchema = baseSchema.keys({
  name: requiredString,
  type: requiredString,
});

const customerLookupSchema = Joi.object({
  overwritingEnabled: requiredFlag,
  autoLinkFields: Joi.array().items(Joi.string().trim()).min(0).optional(),
});

const uriSchema = Joi.string().uri({
  allowRelative: false,
  scheme: 'https',
  domain: {
    tlds: false,
    minDomainSegments: 2,
  },
});

const baseConnectionSettings = Joi.object({
  baseUrl: uriSchema.required().label('Url').messages({
    'string.uriCustomScheme': 'Url must be a valid uri using "https" scheme',
  }),
});

const bigCommerceConnectionSettings = Joi.object({
  storeHash: requiredString,
  clientId: requiredString,
  alwaysUseStoreCreditToRefund: optionalFlag,
});

const awsEventBridgeCustomConfigurations = Joi.object({
  awsAccountId: requiredString,
  region: requiredString,
});

const klaviyoConnectionSettings = Joi.object({
  doNotCreateNewKlaviyoCustomers: Joi.boolean().optional(),
});

const shopifyConnectionSettings = baseConnectionSettings.keys({
  showOrderDetailsInLocalCurrency: Joi.boolean().optional(),
});

const magentoConnectionSettings = baseConnectionSettings.keys({
  deepLinkUrl: uriSchema.optional().allow('').allow(null).messages({
    'string.uriCustomScheme': 'The value must be a valid uri using "https" scheme',
  }),
  fulfilledStatus: optionalString,
});

const zaiusConnectionSettings = Joi.object({
  zaiusPublicKey: requiredString,
});

const qualtricsConnectionSettings = Joi.object({
  qualtricsWebhookUrl: requiredString,
  publicKey: requiredString,
});

const credentialsSchema = Joi.object({
  username: Joi.alternatives()
    .conditional('password', {
      is: requiredString,
      then: requiredString.messages({ 'any.required': 'Enter a username', 'string.empty': 'Enter a username' }),
      otherwise: Joi.string().allow(''),
    })
    .label('User name'),
  password: Joi.alternatives()
    .conditional('username', {
      is: requiredString,
      then: requiredString.messages({ 'any.required': 'Enter a password', 'string.empty': 'Enter a password' }),
      otherwise: Joi.string().allow(''),
    })
    .label('Password'),
  realm: Joi.string().optional().allow('').label('Realm'),
});

const actionEnabledSchema = Joi.object({
  enabled: Joi.boolean().required(),
});

const shopifyCustomActionsSettings = Joi.object({
  refund: actionEnabledSchema.optional(),
  'discount-code': actionEnabledSchema.optional(),
  cancel: actionEnabledSchema.optional(),
  'shopify-update-note': actionEnabledSchema.optional(),
  'shopify-update-order-tags': actionEnabledSchema.optional(),
});

const actionSettingsSchema = Joi.object({
  enabled: Joi.boolean().required(),
  customSettings: shopifyCustomActionsSettings.optional(),
});

/**
 * Webhooks
 */

export const WebhookScheme = baseSchema.keys({
  name: requiredString.messages({ 'any.required': 'Enter a webhook name', 'string.empty': 'Enter a webhook name' }),
  url: uriSchema.required().label('Url').messages({
    'string.uriCustomScheme': 'Enter a valid URL containing "https://"',
    'any.required': 'Enter a valid URL',
    'string.empty': 'Enter a valid URL',
  }),
  events: Joi.array().items(Joi.string()).min(1).required().messages({ 'array.min': 'Select at least one event' }),
  headers: Joi.object().pattern(Joi.string(), Joi.string().allow(null)).optional(),
  credentials: credentialsSchema.optional(),
});

/**
 * Adapter-specific validation schemas
 */

const ShopifySchema = appBaseSchema.keys({
  customerLookupSettings: customerLookupSchema.optional(),
  connectionSettings: shopifyConnectionSettings.required(),
  actionSettings: actionSettingsSchema.required(),
  secrets: Joi.object({
    token: Joi.string().trim().required(),
  }).required(),
});

const MagentoSchema = appBaseSchema.keys({
  customerLookupSettings: customerLookupSchema.optional(),
  connectionSettings: magentoConnectionSettings.required(),
  secrets: Joi.object({
    consumer: Joi.object({
      key: Joi.string().trim().required(),
      secret: Joi.string().trim().required(),
    }).required(),
    token: Joi.object({
      key: Joi.string().trim().required(),
      secret: Joi.string().trim().required(),
    }).required(),
  }).required(),
});

const BigCommerceSchema = appBaseSchema.keys({
  customerLookupSettings: customerLookupSchema.optional(),
  connectionSettings: bigCommerceConnectionSettings.required(),
  actionSettings: actionSettingsSchema.required(),
  secrets: Joi.object({
    token: requiredString,
  }).required(),
});

const SmileSchema = appBaseSchema.keys({
  customerLookupSettings: customerLookupSchema.optional(),
  secrets: Joi.object({
    token: Joi.string().trim().required(),
  }).required(),
});

const RechargeSchema = appBaseSchema.keys({
  customerLookupSettings: customerLookupSchema.optional(),
  secrets: Joi.object({
    token: Joi.string().trim().required(),
  }).required(),
});

const SegmentSchema = appBaseSchema.keys({
  secrets: Joi.object({
    token: Joi.string().trim().required(),
  }).required(),
});

const KlaviyoSchema = appBaseSchema.keys({
  connectionSettings: klaviyoConnectionSettings.required(),
  secrets: Joi.object({
    privateApiKey: Joi.string().trim().required(),
  }).required(),
});

const QualtricsSchema = appBaseSchema.keys({
  connectionSettings: qualtricsConnectionSettings.required(),
});

const ordergrooveActionsSettings = Joi.object({
  'cancel-subscription': actionEnabledSchema.optional(),
  'skip-subscription': actionEnabledSchema.optional(),
});

const OrdergrooveSchema = appBaseSchema.keys({
  connectionSettings: Joi.object({
    publicId: requiredString,
    // if apiKey is present, apiUser is optional
    apiUser: Joi.alternatives().conditional('...secrets.apiKey', {
      is: requiredString,
      then: optionalString,
      otherwise: requiredString,
    }),
  }).required(),
  actionSettings: Joi.object({
    enabled: Joi.boolean().required(),
    customSettings: ordergrooveActionsSettings.optional(),
  }).optional(),
  secrets: Joi.alternatives()
    .try(
      Joi.object({
        apiKey: requiredString,
      }),
      Joi.object({
        hashKey: requiredString,
        apiUserToken: requiredString,
      })
    )
    .required(),
});

const ZaiusSchema = appBaseSchema.keys({
  connectionSettings: zaiusConnectionSettings.required(),
});

const CustomAdapterSchema = appBaseSchema.keys({
  customerLookupSettings: customerLookupSchema.optional(),
  connectionSettings: baseConnectionSettings.required(),
  actionSettings: actionSettingsSchema.optional(),
  secrets: Joi.object({
    signingKey: Joi.string().trim().required(),
    headers: Joi.object().pattern(Joi.string(), Joi.string().allow(null)).optional(),
    credentials: credentialsSchema.optional(),
  }).required(),
});

const AWSEventBridgeSchema = appBaseSchema.keys({
  customConfigurations: awsEventBridgeCustomConfigurations.required(),
});

const LoopReturnsSchema = appBaseSchema.keys({
  secrets: Joi.object({
    apiToken: Joi.string().trim().required(),
  }).required(),
});

export function getSchema(sourceType: ExternalSourceType): Joi.ObjectSchema | undefined {
  switch (sourceType) {
    case ExternalSourceTypes.Shopify:
      return ShopifySchema;

    case ExternalSourceTypes.Magento:
      return MagentoSchema;

    case ExternalSourceTypes.BigCommerce:
      return BigCommerceSchema;

    case ExternalSourceTypes.Klaviyo:
      return KlaviyoSchema;

    case ExternalSourceTypes.LoopReturns:
      return LoopReturnsSchema;

    case ExternalSourceTypes.AwsEventBridge:
      return AWSEventBridgeSchema;

    case ExternalSourceTypes.Ordergroove:
      return OrdergrooveSchema;

    case ExternalSourceTypes.Recharge:
      return RechargeSchema;

    case ExternalSourceTypes.Segment:
      return SegmentSchema;

    case ExternalSourceTypes.Smile:
      return SmileSchema;

    case ExternalSourceTypes.Qualtrics:
      return QualtricsSchema;

    case ExternalSourceTypes.Zaius:
      return ZaiusSchema;

    case ExternalSourceTypes.Custom:
      return CustomAdapterSchema;

    default:
      return undefined;
  }
}
